import Style from './panelStyles.module.scss';
import { enumToDropdownOptions } from 'src/components/uikit/inputs/dropdown/dropdown-helper';
import { AppDropDownWithSuggestionInput } from 'src/components/uikit/inputs/dropdown/appDropDownWithSuggestion';
import { SliderColorSchemeEnum } from 'src/components/uikit/inputs/slider/appSliderInput';
import { isNullOrEmpty } from 'src/helpers/string-helper';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNode } from '@craftjs/core';
import { useContextSelector } from 'use-context-selector';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { SliderInputProps } from '../../types/SliderInput';
import { ControlsGroup } from './uiKit/controlsGroup/controlsGroup';
import { SettingsInput } from './uiKit/input/settingsInput';
import { PanelContainer } from './uiKit/panelContainer/panelContainer';
import { AppCheckboxInput } from 'uikit/inputs';
import { LayoutValidationContext, ValidationEnum } from '../../validationContext/layoutValidation.context';

export const SliderInputPanel = () => {
  const { t } = useScopedTranslation('GeneralFormBuilder.Panels.SliderField');
  const {
    id,
    actions: { setProp },
    props,
  } = useNode((node) => ({
    props: node.data.props as SliderInputProps,
  }));

  const dataKeyValidationError = useContextSelector(LayoutValidationContext, (x) => x.layoutValidationResult[id]);

  const errorMessage = useMemo(
    () => (dataKeyValidationError?.type === ValidationEnum.SliderMarks ? dataKeyValidationError?.message : undefined),
    [dataKeyValidationError?.message, dataKeyValidationError?.type],
  );

  // NOTE: This state allows to erase the "MinValue" field completely
  const [minValue, setMinValue] = useState<number | undefined>(props.minValue);
  useEffect(() => {
    if (minValue === undefined) return;
    setProp((setProps: SliderInputProps) => {
      setProps.minValue = Math.floor(minValue) > props.maxValue ? props.maxValue - 1 : Math.floor(minValue);
    }, 500);
  }, [minValue, props.maxValue, setProp]);

  // NOTE: This state allows to erase the "MaxValue" field completely
  const [maxValue, setMaxValue] = useState<number | undefined>(props.maxValue);
  useEffect(() => {
    if (maxValue === undefined) return;
    setProp((setProps: SliderInputProps) => {
      setProps.maxValue = Math.floor(maxValue) < props.minValue ? props.minValue + 1 : Math.floor(maxValue);
    }, 500);
  }, [maxValue, props.minValue, setProp]);

  // NOTE: This state allows to erase the "StartPoint" field completely
  const [startPoint, setStartPoint] = useState<number | undefined>(props.startPoint);

  const getCorrectStartPoint = useCallback(
    (input?: number): number | undefined => {
      if (input === undefined) return undefined;

      if (input < props.minValue) return props.minValue;
      if (input > props.maxValue) return props.maxValue;

      return input;
    },
    [props.minValue, props.maxValue],
  );

  useEffect(() => {
    setProp((setProps: SliderInputProps) => {
      const startValue = getCorrectStartPoint(startPoint);
      setProps.startPoint = startValue;
    }, 500);
  }, [startPoint, setProp, getCorrectStartPoint]);

  // NOTE: This state allows to erase the "Step" field completely
  const [stepValue, setStepValue] = useState<number | undefined>(props.step);
  useEffect(() => {
    if (stepValue === undefined) return;
    setProp((setProps: SliderInputProps) => {
      setProps.step = +stepValue.toFixed(2);
    }, 500);
  }, [stepValue, setProp]);

  // NOTE: This state allows to erase the "Mark's step" field completely
  const [markStepValue, setMarkStepValue] = useState<number | undefined>(props.marksStep);
  useEffect(() => {
    if (markStepValue === undefined) return;
    setProp((setProps: SliderInputProps) => {
      setProps.marksStep = Math.floor(markStepValue);
    }, 500);
  }, [markStepValue, setProp]);

  const clearStartPointAndDefaultValue = useCallback(() => {
    setStartPoint(undefined);
    setProp((p: SliderInputProps) => {
      p.defaultValue = undefined;
      p.startPoint = undefined;
    });
  }, [setProp]);

  const handleMaxValueBlur = useCallback(
    (newMaxValue: number) => {
      setMaxValue(newMaxValue);
      if (startPoint && maxValue && maxValue < startPoint) {
        clearStartPointAndDefaultValue();
      }
    },
    [clearStartPointAndDefaultValue, maxValue, startPoint],
  );

  const handleMinValueBlur = useCallback(
    (newMinValue: number) => {
      setMinValue(newMinValue);
      if (startPoint && minValue && minValue > startPoint) {
        clearStartPointAndDefaultValue();
      }
    },
    [clearStartPointAndDefaultValue, minValue, startPoint],
  );

  return (
    <PanelContainer header={t('Header')} panelName={'General'}>
      <ControlsGroup contentClassName={Style.flexContainer}>
        <AppCheckboxInput
          label={t('ShowNumberInput')}
          checked={!!props.showNumberInput}
          onChange={(e) =>
            setProp((setProps: SliderInputProps) => {
              setProps.showNumberInput = e.target.checked ? e.target.checked : undefined;
            }, 500)
          }
        />
      </ControlsGroup>
      <ControlsGroup header={t('MinMaxLabel')} contentClassName={Style.gridContainer}>
        <SettingsInput
          type={'number'}
          placeholder={t('MinValue')}
          value={minValue}
          onChange={(e) => {
            setMinValue(isNullOrEmpty(e.target.value) ? undefined : +e.target.value);
          }}
          onBlur={() => handleMinValueBlur(props.minValue)}
        />
        <SettingsInput
          type={'number'}
          placeholder={t('MaxValue')}
          value={maxValue}
          onChange={(e) => {
            setMaxValue(isNullOrEmpty(e.target.value) ? undefined : +e.target.value);
          }}
          onBlur={() => handleMaxValueBlur(props.maxValue)}
        />
      </ControlsGroup>
      <ControlsGroup header={t('Step')} contentClassName={Style.flexContainer}>
        <SettingsInput
          type={'number'}
          value={stepValue}
          onChange={(e) => {
            setStepValue(isNullOrEmpty(e.target.value) ? undefined : +e.target.value);
          }}
          onBlur={() => setStepValue(props.step)}
        />
      </ControlsGroup>
      <ControlsGroup
        collapsible
        expanded={!props.hideMarks}
        header={t('MarkStep')}
        contentClassName={Style.flexContainer}
        onOpen={() => {
          setMarkStepValue(1);
          setProp((prop: SliderInputProps) => {
            prop.hideMarks = undefined;
          }, 500);
        }}
        onClose={() => {
          setMarkStepValue(undefined);
          setProp((prop: SliderInputProps) => {
            prop.hideMarks = true;
          }, 500);
        }}
      >
        <SettingsInput
          type={'number'}
          value={markStepValue}
          onChange={(e) => {
            setMarkStepValue(isNullOrEmpty(e.target.value) ? undefined : +e.target.value);
          }}
          onBlur={() => setMarkStepValue(props.marksStep)}
          error={errorMessage}
        />
      </ControlsGroup>
      <ControlsGroup
        collapsible
        header={t('StartPoint')}
        contentClassName={Style.flexContainer}
        expanded={props.startPoint !== undefined}
        onOpen={() => setStartPoint(0)}
        onClose={() => setStartPoint(undefined)}
      >
        <SettingsInput
          type={'number'}
          value={startPoint}
          onChange={(e) => {
            setStartPoint(isNullOrEmpty(e.target.value) ? undefined : +e.target.value);
          }}
          onBlur={() => setStartPoint(props.startPoint)}
        />
      </ControlsGroup>
      <ControlsGroup
        collapsible
        expanded={!!props.captionStartText}
        header={t('CaptionStartText')}
        contentClassName={Style.flexContainer}
        onOpen={() => {
          setProp((prop: SliderInputProps) => {
            prop.captionStartText = 'Left text';
          }, 500);
        }}
        onClose={() => {
          setProp((prop: SliderInputProps) => {
            prop.captionStartText = undefined;
          }, 500);
        }}
      >
        <SettingsInput
          value={props.captionStartText}
          onChange={(e) => {
            setProp((setProps: SliderInputProps) => {
              setProps.captionStartText = e.target.value;
            }, 500);
          }}
        />
      </ControlsGroup>
      <ControlsGroup
        collapsible
        expanded={!!props.captionCenterText}
        contentClassName={Style.flexContainer}
        header={t('CaptionCenterText')}
        onOpen={() => {
          setProp((prop: SliderInputProps) => {
            prop.captionCenterText = 'Center text';
          }, 500);
        }}
        onClose={() => {
          setProp((prop: SliderInputProps) => {
            prop.captionCenterText = undefined;
          }, 500);
        }}
      >
        <SettingsInput
          value={props.captionCenterText}
          onChange={(e) => {
            setProp((setProps: SliderInputProps) => {
              setProps.captionCenterText = e.target.value;
            }, 500);
          }}
        />
      </ControlsGroup>
      <ControlsGroup
        collapsible
        expanded={!!props.captionEndText}
        contentClassName={Style.flexContainer}
        header={t('CaptionEndText')}
        onOpen={() => {
          setProp((prop: SliderInputProps) => {
            prop.captionEndText = 'Right text';
          }, 500);
        }}
        onClose={() => {
          setProp((prop: SliderInputProps) => {
            prop.captionEndText = undefined;
          }, 500);
        }}
      >
        <SettingsInput
          value={props.captionEndText}
          onChange={(e) => {
            setProp((setProps: SliderInputProps) => {
              setProps.captionEndText = e.target.value;
            }, 500);
          }}
        />
      </ControlsGroup>
      <ControlsGroup header={t('ColorScheme')} contentClassName={Style.flexContainer}>
        <AppDropDownWithSuggestionInput
          isRequired={true}
          options={enumToDropdownOptions(SliderColorSchemeEnum)}
          value={
            enumToDropdownOptions(SliderColorSchemeEnum).find(
              (x) => x.key === SliderColorSchemeEnum[props.colorScheme],
            ) ??
            enumToDropdownOptions(SliderColorSchemeEnum).find(
              (x) => x.key === SliderColorSchemeEnum.SingleColor.toString(),
            )
          }
          onChange={(x) => {
            setProp((setProps: SliderInputProps) => {
              const key = x?.key as keyof typeof SliderColorSchemeEnum;
              setProps.colorScheme = SliderColorSchemeEnum[key];
            });
          }}
        />
      </ControlsGroup>
    </PanelContainer>
  );
};
